/**
 * ResizableDivider Component
 * 
 * A draggable divider between master and detail panels.
 * Provides visual feedback during resize and handles mouse events.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import { GripVertical } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface ResizableDividerProps {
  /** Called when resize starts */
  onResizeStart?: () => void;
  
  /** Called during resize with delta X */
  onResize: (deltaX: number) => void;
  
  /** Called when resize ends */
  onResizeEnd?: () => void;
  
  /** Divider orientation */
  orientation?: 'vertical' | 'horizontal';
  
  /** Additional className */
  className?: string;
  
  /** Whether divider is disabled */
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ResizableDivider({
  onResizeStart,
  onResize,
  onResizeEnd,
  orientation = 'vertical',
  className = '',
  disabled = false,
}: ResizableDividerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const lastPositionRef = useRef(0);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    lastPositionRef.current = orientation === 'vertical' ? e.clientX : e.clientY;
    onResizeStart?.();
    
    // Add global event listeners
    document.body.style.cursor = orientation === 'vertical' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  }, [disabled, orientation, onResizeStart]);
  
  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const currentPosition = orientation === 'vertical' ? e.clientX : e.clientY;
      const delta = currentPosition - lastPositionRef.current;
      lastPositionRef.current = currentPosition;
      onResize(delta);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      onResizeEnd?.();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, orientation, onResize, onResizeEnd]);
  
  // Touch support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    
    e.preventDefault();
    setIsDragging(true);
    
    const touch = e.touches[0];
    lastPositionRef.current = orientation === 'vertical' ? touch.clientX : touch.clientY;
    onResizeStart?.();
  }, [disabled, orientation, onResizeStart]);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const currentPosition = orientation === 'vertical' ? touch.clientX : touch.clientY;
      const delta = currentPosition - lastPositionRef.current;
      lastPositionRef.current = currentPosition;
      onResize(delta);
    };
    
    const handleTouchEnd = () => {
      setIsDragging(false);
      onResizeEnd?.();
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, orientation, onResize, onResizeEnd]);
  
  const isVertical = orientation === 'vertical';
  
  return (
    <div
      className={`
        group relative flex-shrink-0
        ${isVertical ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize'}
        ${isDragging ? 'bg-primary-500' : 'bg-[var(--hover-tint)] hover:bg-primary-400'}
        ${disabled ? 'cursor-default opacity-50' : ''}
        transition-colors
        ${className}
      `}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role="separator"
      aria-orientation={orientation}
      aria-valuenow={undefined}
      tabIndex={disabled ? -1 : 0}
    >
      {/* Grip handle - larger touch target */}
      <div
        className={`
          absolute z-10
          ${isVertical 
            ? 'inset-y-0 -left-1 -right-1 flex items-center justify-center'
            : 'inset-x-0 -top-1 -bottom-1 flex items-center justify-center'
          }
        `}
      >
        <div
          className={`
            flex items-center justify-center 
            ${isVertical ? 'w-3 h-8' : 'w-8 h-3'}
            ${isDragging ? 'bg-primary-600' : 'bg-[var(--rule-bright)] group-hover:bg-primary-500'}
            transition-colors
          `}
        >
          <GripVertical
            className={`
              h-4 w-4 text-white
              ${!isVertical ? 'rotate-90' : ''}
            `}
          />
        </div>
      </div>
      
      {/* Expand hit area */}
      <div
        className={`
          absolute
          ${isVertical ? 'inset-y-0 -left-2 -right-2' : 'inset-x-0 -top-2 -bottom-2'}
        `}
      />
    </div>
  );
}

export default ResizableDivider;
